import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../config/axios";
import { customAxiosApi, globalConfig } from "../../config/axios-retry";
import Toast from "../../shared/toast/Toast";
import { SubscriptionPurchase } from "../../utils/interfaces/product/ProductPurchase";
import { getPartnerInfo } from "../partner/partner";
import { FULLTIME_PARTNER_ID_KEY } from "../../utils/constants/constants";
import { FrontendPaymentSource } from "src/utils/enums/FrontendPaymentSource";
import { SubscriptionInitialState } from "src/utils/interfaces/payment/SubscriptionInitialState";
import { RootState } from "../store";
import { isNationalDayOfferActive } from "src/components/subscription/pricing-v2/nationalDayPlans";
import { SubscriptionBillingCycleEnum, SubscriptionPlanCategoryEnum } from "src/utils/enums/PartnerSubscription";
import { PreparePurchaseSubscriptionRequest } from "src/utils/interfaces/payment/PreparePurchaseProductRequest";

const appKey = 'appPartnerSubscriptions';
const apiBaseUrl = 'pa/ft/partners/subscription-plans';
const apiBaseUrlDefault = 'pa/ft/partners/default-subscription-plans';
const apiExclusivePlansUrl = 'pa/ft/partners/exclusive-subscription-plans';
const apiPmaExclusivePlansUrl = 'pma/ft/payment/exclusive-subscription-plans';
const apiPrepareRequestPaUrl = 'pa/ft/product/prepare-purchase';
const apiPrepareRequestPmaUrl = 'pma/ft/payment/prepare-purchase';
const apiBankQuotationUrl = 'pa/ft/payment/bank-quotations';
const apiFreeSubscriptionUrl = 'pa/ft/product/subscribe-for-free';
const apiSubscribeWatanUrl = 'pa/ft/product/subscribe-watan';

export const getSubscriptions = createAsyncThunk(`${appKey}/getSubscriptions`, async () => {
    const response = await customAxiosApi.get(`${apiBaseUrl}`, globalConfig);
    return response.data;
})

export const getDefaultSubscriptions = createAsyncThunk(`${appKey}/getDefaultSubscriptions`, async () => {
    const response = await customAxiosApi.get(`${apiBaseUrlDefault}`, globalConfig);
    return response.data;
})

export const getExclusiveSubscriptionPlans = createAsyncThunk<
    any,
    { ftPartnerExternalId?: string } | void
>(
    `${appKey}/getExclusiveSubscriptionPlans`,
    async (query) => {
        const ftPartnerExternalId =
            query && typeof query === "object" ? query.ftPartnerExternalId : undefined;
        if (ftPartnerExternalId) {
            try {
                const response = await axios.get(
                    `${apiPmaExclusivePlansUrl}?ftPartnerExternalId=${ftPartnerExternalId}`
                );
                return response.data;
            } catch {
                try {
                    const response = await customAxiosApi.get(`${apiExclusivePlansUrl}`, globalConfig);
                    return response.data;
                } catch {
                    return { data: [] };
                }
            }
        }
        const response = await customAxiosApi.get(`${apiExclusivePlansUrl}`, globalConfig);
        return response.data;
    },
    {
        condition: (_, { getState }) => {
            if (!isNationalDayOfferActive()) return false;
            const { exclusiveSubscriptionPlansFetched, isLoadingExclusivePlans } = (
                getState() as RootState
            ).subscriptions;
            return !exclusiveSubscriptionPlansFetched && !isLoadingExclusivePlans;
        },
    }
)

export const preparePurchaseRequest = createAsyncThunk(`${appKey}/preparePurchaseRequest`, async (body: PreparePurchaseSubscriptionRequest) => {
    const response = await customAxiosApi.post(`${body.ftPartnerExternalId ? apiPrepareRequestPmaUrl : apiPrepareRequestPaUrl}/${body.planId}`, { ...body }, globalConfig);
    return response.data.data;
})

export const getBankQuotation = createAsyncThunk(`${appKey}/getBankQuotation`, async (body: SubscriptionPurchase) => {
    const response = await customAxiosApi.get(`${apiBankQuotationUrl}/${body.planId}?billingCycle=${body.billingCycle}&ftPartnerExternalId=${localStorage.getItem(FULLTIME_PARTNER_ID_KEY) || body.ftPartnerExternalId}&jobsCount=${body.jobsCount}`, globalConfig);
    return response.data.data;
})

export const subscribeToWatanPlan = createAsyncThunk(`${appKey}/subscribeToWatanPlan`, async (planId: string, { rejectWithValue }) => {
    try {
        const response = await customAxiosApi.post(`${apiSubscribeWatanUrl}/${planId}`, {}, globalConfig);
        return response.data?.data;
    } catch (error) {
        const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        return rejectWithValue(message || '');
    }
})

export const subscribeFreeSubscription = createAsyncThunk(`${appKey}/subscribeFreeSubscription`, async (body: any, { dispatch }) => {
    const response = await customAxiosApi.post(apiFreeSubscriptionUrl, { ...body }, globalConfig);
    if (response.status === 201) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})

export const proceedPurchase = createAction<SubscriptionPurchase>(`${appKey}/proceedPurchase`);
export const setFrontendSource = createAction<FrontendPaymentSource>(`${appKey}/setFrontendSource`);
export const setSelectedBillingCycle = createAction<SubscriptionBillingCycleEnum>(`${appKey}/setSelectedBillingCycle`);
export const setSelectedPlanInModal = createAction<SubscriptionPlanCategoryEnum>(`${appKey}/setSelectedPlanInModal`);
export const setPurpleNoteInModal = createAction<string>(`${appKey}/setPurpleNoteInModal`);
export const addNumberOfJobs = createAction<number>(`${appKey}/addNumberOfJobs`);
export const subtractNumberOfJobs = createAction<number>(`${appKey}/subtractNumberOfJobs`);
export const setSelectedNumberOfJobs = createAction<number>(`${appKey}/setSelectedNumberOfJobs`);
export const setShowSubscribedDetailsDropdown = createAction<boolean>(`${appKey}/setShowSubscribedDetailsDropdown`);
export const setShowVatModalAfterPayment = createAction<boolean>(`${appKey}/setShowVatModalAfterPayment`);


const initialState: SubscriptionInitialState = {
    isLoading: true,
    data: null,
    subscriptionPlans: [],
    exclusiveSubscriptionPlans: [],
    exclusiveSubscriptionPlansFetched: false,
    isLoadingExclusivePlans: false,
    purchaseRequestLoader: true,
    purchaseRequest: null,
    preparePurchaseData: null,
    showQuotationLoader: false,
    frontendSource: null,
    selectedBillingCycle: SubscriptionBillingCycleEnum.MONTHLY,
    selectedPlanInModal: SubscriptionPlanCategoryEnum.CORE,
    purpleNoteInModal: '',
    selectedNumberOfJobs: 1,
    showSubscribedDetailsDropdown: false,
    showVatModalAfterPayment: false
}

export const appPartnerSubscriptionsSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(getSubscriptions.fulfilled, (state, action) => {
                state.data = action.payload;
                state.subscriptionPlans = action.payload?.data;
                state.isLoading = false;
            })
            .addCase(getSubscriptions.rejected, (state, action) => {
                state.isLoading = false;
                // Toast.Error({ content: action.error?.message })
            })
            .addCase(getDefaultSubscriptions.fulfilled, (state, action) => {
                state.data = action.payload;
                state.subscriptionPlans = action.payload?.data;
                state.isLoading = false;
            })
            .addCase(getDefaultSubscriptions.rejected, (state, action) => {
                state.isLoading = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(getExclusiveSubscriptionPlans.pending, (state) => {
                state.isLoadingExclusivePlans = true;
            })
            .addCase(getExclusiveSubscriptionPlans.fulfilled, (state, action) => {
                state.exclusiveSubscriptionPlans = action.payload?.data || [];
                state.exclusiveSubscriptionPlansFetched = true;
                state.isLoadingExclusivePlans = false;
            })
            .addCase(getExclusiveSubscriptionPlans.rejected, (state) => {
                state.exclusiveSubscriptionPlans = [];
                state.exclusiveSubscriptionPlansFetched = true;
                state.isLoadingExclusivePlans = false;
            })
            .addCase(preparePurchaseRequest.fulfilled, (state, action) => {
                state.purchaseRequest = action.payload;
                state.purchaseRequestLoader = false;
            })
            .addCase(preparePurchaseRequest.rejected, (state, action) => {
                state.purchaseRequestLoader = false;
                Toast.Error({ content: action.error?.message })
            })
            .addCase(proceedPurchase, (state, action) => {
                state.preparePurchaseData = action.payload;
            })
            .addCase(getBankQuotation.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message })
            })
            .addCase(subscribeFreeSubscription.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message })
            })
            .addCase(setFrontendSource, (state, action) => {
                state.frontendSource = action.payload;
            })
            .addCase(setSelectedBillingCycle, (state, action) => {
                state.selectedBillingCycle = action.payload;
            })
            .addCase(setSelectedPlanInModal, (state, action) => {
                state.selectedPlanInModal = action.payload;
            })
            .addCase(setPurpleNoteInModal, (state, action) => {
                state.purpleNoteInModal = action.payload;
            })
            .addCase(setSelectedNumberOfJobs, (state, action) => {
                state.selectedNumberOfJobs = action.payload;
            })
            .addCase(setShowSubscribedDetailsDropdown, (state, action) => {
                state.showSubscribedDetailsDropdown = action.payload;
            })
            .addCase(setShowVatModalAfterPayment, (state, action) => {
                state.showVatModalAfterPayment = action.payload;
            })
            .addCase(addNumberOfJobs, (state, action) => {
                let newCount = state.selectedNumberOfJobs + 1;
                if (newCount <= 5) {
                    state.selectedNumberOfJobs = newCount;
                }
            })
            .addCase(subtractNumberOfJobs, (state, action) => {
                let newCount = state.selectedNumberOfJobs - 1;
                if (newCount >= 1) {
                    state.selectedNumberOfJobs = newCount;
                }
            })

    }
})

export default appPartnerSubscriptionsSlice.reducer
